import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import styles from './CadastroDespesaColheita.module.css';
import { 
    FiX, FiSave, FiAlertCircle, FiSettings, 
    FiDollarSign, FiTag, FiCalendar, FiTrash2, 
    FiList, FiPlus, FiEdit2
} from 'react-icons/fi';
import Swal from 'sweetalert2';

const CadastroDespesaColheita = ({ equipeId, nomeEquipe, onClose }) => {
    const [tipoDespesa, setTipoDespesa] = useState('');
    const [nomeDespesa, setNomeDespesa] = useState('');
    const [valor, setValor] = useState('');
    const [data, setData] = useState(new Date().toISOString().split('T')[0]);
    const [descricao, setDescricao] = useState('');
    
    const [despesas, setDespesas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const fetchDespesas = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/api/despesas-equipe/equipe/${equipeId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDespesas(response.data);
        } catch (err) {
            console.error(err);
        }
    }, [equipeId]);

    useEffect(() => {
        fetchDespesas();
    }, [fetchDespesas]);

    const limparFormulario = () => {
        setNomeDespesa('');
        setTipoDespesa('');
        setValor('');
        setDescricao('');
        setData(new Date().toISOString().split('T')[0]);
        setEditingId(null);
    };

    const handleSubmit = async () => {
        if (!nomeDespesa || !tipoDespesa || !valor || !data) {
            Swal.fire({
                title: 'Atenção',
                text: 'Preencha os campos obrigatórios.',
                icon: 'warning',
                didOpen: () => {
                    Swal.getContainer().style.zIndex = '3000';
                }
            });
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const payload = {
                equipe_id: equipeId,
                nome_despesa: nomeDespesa,
                tipo_despesa: tipoDespesa,
                valor: parseFloat(valor),
                data_despesa: data,
                observacoes: descricao
            };

            if (editingId) {
                await axios.put(`${API_BASE_URL}/api/despesas-equipe/${editingId}`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                await Swal.fire({
                    title: 'Atualizado!',
                    text: 'A despesa foi atualizada com sucesso.',
                    icon: 'success',
                    confirmButtonColor: '#16a34a',
                    didOpen: () => {
                        Swal.getContainer().style.zIndex = '3000';
                    }
                });
            } else {
                await axios.post(`${API_BASE_URL}/api/despesas-equipe`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                await Swal.fire({
                    title: 'Sucesso!',
                    text: 'Gasto registrado com sucesso.',
                    icon: 'success',
                    confirmButtonColor: '#16a34a',
                    didOpen: () => {
                        Swal.getContainer().style.zIndex = '3000';
                    }
                });
            }

            limparFormulario();
            await fetchDespesas();
            
        } catch (err) {
            console.error(err);
            Swal.fire({
                title: 'Erro',
                text: editingId ? 'Erro ao atualizar despesa.' : 'Erro ao salvar despesa.',
                icon: 'error',
                confirmButtonColor: '#ef4444',
                didOpen: () => {
                    Swal.getContainer().style.zIndex = '3000';
                }
            });
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (despesa) => {
        setEditingId(despesa.id);
        setNomeDespesa(despesa.nome_despesa || '');
        setTipoDespesa(despesa.tipo_despesa || '');
        setValor(despesa.valor ?? '');
        setDescricao(despesa.observacoes || '');

        const dataFormatada = despesa.data_despesa
            ? String(despesa.data_despesa).split('T')[0]
            : new Date().toISOString().split('T')[0];

        setData(dataFormatada);

        const modalBody = document.querySelector(`.${styles.modalBody}`);
        if (modalBody) {
            modalBody.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleCancelEdit = () => {
        limparFormulario();
    };

    const handleDelete = async (despesa) => {
        const result = await Swal.fire({
            title: 'Excluir lançamento?',
            text: `A despesa "${despesa.nome_despesa}" será excluída permanentemente.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sim, excluir',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#64748b',
            reverseButtons: true,
            focusCancel: true,
            didOpen: () => {
                Swal.getContainer().style.zIndex = '3000';
            }
        });

        if (!result.isConfirmed) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('token');

            await axios.delete(`${API_BASE_URL}/api/despesas-equipe/${despesa.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (editingId === despesa.id) {
                limparFormulario();
            }

            await fetchDespesas();

            await Swal.fire({
                title: 'Excluído!',
                text: 'A despesa foi excluída com sucesso.',
                icon: 'success',
                confirmButtonColor: '#16a34a',
                didOpen: () => {
                    Swal.getContainer().style.zIndex = '3000';
                }
            });
        } catch (err) {
            console.error(err);
            Swal.fire({
                title: 'Erro',
                text: 'Não foi possível excluir a despesa.',
                icon: 'error',
                confirmButtonColor: '#ef4444',
                didOpen: () => {
                    Swal.getContainer().style.zIndex = '3000';
                }
            });
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
    };

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                <div className={styles.modalHeader}>
                    <div className={styles.headerTitleGroup}>
                        <div className={styles.iconCircle}>
                            <FiSettings size={20} />
                        </div>
                        <div>
                            <h2>Gestão Financeira</h2>
                            <p>Equipe: <strong>{nomeEquipe}</strong></p>
                        </div>
                    </div>
                    <button className={styles.btnClose} onClick={onClose}>
                        <FiX size={24} />
                    </button>
                </div>

                <div className={styles.modalBody}>
                    <div className={`${styles.sectionCard} ${editingId ? styles.sectionCardEditing : ''}`}>
                        <div className={styles.sectionHeader}>
                            {editingId ? <FiEdit2 /> : <FiPlus />}
                            <span>{editingId ? 'Editar Lançamento' : 'Novo Lançamento'}</span>
                        </div>

                        {editingId && (
                            <div className={styles.editingNotice}>
                                <div className={styles.editingNoticeIcon}>
                                    <FiEdit2 size={18} />
                                </div>
                                <div>
                                    <strong>Modo de edição</strong>
                                    <span>Altere os dados abaixo e salve para atualizar este lançamento.</span>
                                </div>
                            </div>
                        )}
                        
                        <div className={styles.formGrid}>
                            <div className={styles.formGroupFull}>
                                <label><FiTag /> Nome da Despesa</label>
                                <input 
                                    type="text" 
                                    placeholder="Ex: Pagamento Mensal de Operador..." 
                                    value={nomeDespesa}
                                    onChange={(e) => setNomeDespesa(e.target.value)}
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label>Categoria</label>
                                <select value={tipoDespesa} onChange={(e) => setTipoDespesa(e.target.value)}>
                                    <option value="">Selecione...</option>
                                    <option value="Salário / Pagamento">Salário / Pagamento</option>
                                    <option value="Alimentação / Comida">Alimentação / Comida</option>
                                    <option value="Diária / Viagem">Diária / Viagem</option>
                                    <option value="EPI / Botas / Luvas">EPI / Botas / Luvas</option>
                                    <option value="Remédios / Saúde">Remédios / Saúde</option>
                                    <option value="Outros Gastos">Outros Gastos</option>
                                </select>
                            </div>

                            <div className={styles.formGroup}>
                                <label><FiDollarSign /> Valor</label>
                                <input 
                                    type="number" 
                                    placeholder="0,00"
                                    value={valor}
                                    onChange={(e) => setValor(e.target.value)}
                                />
                            </div>

                            <div className={styles.formGroupFull}>
                                <label><FiCalendar /> Data do Gasto</label>
                                <input 
                                    type="date" 
                                    value={data}
                                    onChange={(e) => setData(e.target.value)}
                                />
                            </div>

                            <div className={styles.formGroupFull}>
                                <label>Observações</label>
                                <textarea 
                                    rows="2" 
                                    placeholder="Informações adicionais..."
                                    value={descricao}
                                    onChange={(e) => setDescricao(e.target.value)}
                                />
                            </div>
                        </div>

                        {editingId ? (
                            <div className={styles.editActions}>
                                <button 
                                    className={styles.btnCancelEdit} 
                                    onClick={handleCancelEdit}
                                    disabled={loading}
                                >
                                    <FiX size={18} /> Cancelar
                                </button>

                                <button 
                                    className={styles.btnSaveEdit} 
                                    onClick={handleSubmit} 
                                    disabled={loading}
                                >
                                    <FiSave size={18} /> {loading ? 'Processando...' : 'Salvar Alterações'}
                                </button>
                            </div>
                        ) : (
                            <button className={styles.btnSave} onClick={handleSubmit} disabled={loading}>
                                <FiSave size={18} /> {loading ? 'Processando...' : 'Confirmar Lançamento'}
                            </button>
                        )}
                    </div>

                    <div className={styles.listSection}>
                        <div className={styles.sectionHeader}>
                            <FiList /> <span>Histórico de Despesas</span>
                        </div>

                        <div className={styles.tableResponsive}>
                            <table className={styles.elegantTable}>
                                <thead>
                                    <tr>
                                        <th>Data</th>
                                        <th>Descrição</th>
                                        <th>Categoria</th>
                                        <th className={styles.textRight}>Valor</th>
                                        <th className={styles.textCenter}>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {despesas.length > 0 ? despesas.map(d => (
                                        <tr key={d.id} className={editingId === d.id ? styles.rowEditing : ''}>
                                            <td className={styles.dateCell} data-label="Data">
                                                {new Date(d.data_despesa).toLocaleDateString('pt-BR', {timeZone: 'UTC'})}
                                            </td>
                                            <td data-label="Descrição">
                                                <span className={styles.expenseName}>{d.nome_despesa}</span>
                                            </td>
                                            <td data-label="Categoria">
                                                <span className={styles.categoryBadge}>{d.tipo_despesa}</span>
                                            </td>
                                            <td className={`${styles.valueCell} ${styles.textRight}`} data-label="Valor">
                                                {formatCurrency(d.valor)}
                                            </td>
                                            <td className={styles.textCenter} data-label="Ações">
                                                <div className={styles.actionButtons}>
                                                    <button 
                                                        className={styles.btnActionEdit}
                                                        onClick={() => handleEdit(d)}
                                                        disabled={loading}
                                                        title="Editar despesa"
                                                        aria-label={`Editar ${d.nome_despesa}`}
                                                    >
                                                        <FiEdit2 size={16} />
                                                    </button>

                                                    <button 
                                                        className={styles.btnActionDelete}
                                                        onClick={() => handleDelete(d)}
                                                        disabled={loading}
                                                        title="Excluir despesa"
                                                        aria-label={`Excluir ${d.nome_despesa}`}
                                                    >
                                                        <FiTrash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="5" className={styles.emptyState}>
                                                <FiAlertCircle size={24} />
                                                <p>Nenhuma despesa encontrada para esta equipe.</p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CadastroDespesaColheita;
